<template>
  <AppDroplist
    :dropdown-width="isStartTargetWithMilestones ? '260px' : '280px'"
    :hide-on-click="false"
    :theme="DROP_LIST_THEMES.LIGHT"
    append-to=".slu-Main"
    fit-content-width
    placement="bottom"
    trigger="mouseenter"
  >
    <template #button>
      <AppInfoMessage class="si-Trigger">
        <span>
          {{
            $t('objectives.predicted', {
              symbol: predictedScore > MAX_GRADE ? MATH_SYMBOLS.GREATER_THAN_OR_EQUAL : null
            })
          }}
          <span :style="{ color: `var(${getGradeColorVariable(gradeColor)})` }">
            {{ roundValue(resolvedPredictedScore) }}%
          </span>
        </span>
      </AppInfoMessage>
    </template>
    <div
      :style="{
        '--color': `var(${getGradeColorVariable(gradeColor)})`
      }"
      class="si-TooltipContainer"
    >
      <span v-if="isStartTargetWithMilestones" class="si-TooltipTitle si-TooltipTitle-bold">
        <i18n-t
          class="si-TooltipTitle si-TooltipTitle-bold"
          keypath="objectives.status_indicator.description.start_target_milestones"
          scope="global"
          tag="div"
        >
          <template #grade>
            <span> {{ roundValue(resolvedPredictedScore) }}% </span>
          </template>
        </i18n-t>
      </span>
      <template v-else>
        <i18n-t
          class="si-TooltipTitle si-TooltipTitle-bold"
          keypath="objectives.status_indicator.description.predicted"
          scope="global"
          tag="div"
        >
          <template #predicted>
            <span>{{ getSimplifiedNumber(predictedScore)[PLURALIZATION_KEYS.SIMPLIFIED] }}%</span>
          </template>
          <template #grade>
            <span>{{ getSimplifiedNumber(grade)[PLURALIZATION_KEYS.SIMPLIFIED] }}</span>
          </template>
          <template #passed>
            {{ Math.min(roundValue(passed), MAX_GRADE) }}
          </template>
        </i18n-t>

        <i18n-t
          class="si-TooltipTitle"
          keypath="objectives.status_indicator.description.grade"
          scope="global"
          tag="div"
        >
          <template #grade>
            <span>{{ getSimplifiedNumber(grade)[PLURALIZATION_KEYS.SIMPLIFIED] }}%</span>
          </template>
        </i18n-t>

        <i18n-t
          class="si-TooltipTitle"
          keypath="objectives.status_indicator.description.passed"
          scope="global"
          tag="div"
        >
          <template #passed>
            <b>{{ roundValue(passed) > MAX_GRADE ? '100' : roundValue(passed) }}%</b>
          </template>
        </i18n-t>
        <div class="si-Chart">
          <LineChart
            v-if="chartData"
            :aspect-ratio="1.6"
            :chart-data="chartData"
            :chart-type="CHART_TYPES.predictedScorePreview"
            :grade-values="gradeValues"
            full-size-chart
            set-y-from0-to100
            without-background
          />
        </div>
      </template>
    </div>
  </AppDroplist>
</template>

<script>
import dayjs from 'dayjs'
import { isEmpty } from 'lodash'
import { defineComponent } from 'vue'
import { mapState } from 'vuex'

import { DROP_LIST_THEMES } from '@/utils/components-configurations/app-droplist'
import { compensateTimeZone } from '@/utils/date'
import { CHART_TYPES } from '@/utils/objective-chart'
import { isKR } from '@/utils/objective-types'
import { getMinMaxDates, getSelectedIntervalDates } from '@/utils/okr-element-dates'
import { isStartTargetProgress } from '@/utils/okr-element-values'
import { getGradeColorStyle, getGradeColorVariable } from '@/utils/okr-elements/grade'
import { MATH_SYMBOLS } from '@/utils/okr-elements/math'
import { getSimplifiedNumber, PLURALIZATION_KEYS } from '@/utils/pluralization'

import AppDroplist from '@/components/AppDroplist'
import LineChart from '@/components/LineChart/LineChart'
import AppInfoMessage from '@/components/ui/AppInfoMessage'

const MIN_GRADE = 0
const MAX_GRADE = 100

export default defineComponent({
  name: 'StatusIndicator',
  components: {
    AppInfoMessage,
    LineChart,
    AppDroplist
  },

  props: {
    predictedScore: {
      type: [String, Number],
      default: 0
    },

    passed: {
      type: [String, Number],
      default: 0
    },

    grade: {
      type: [String, Number],
      default: MIN_GRADE
    },

    gradeColor: {
      type: String,
      required: true
    },

    okrElement: {
      type: Object,
      required: true
    }
  },

  computed: {
    ...mapState('system', {
      settings: state => state.settings
    }),

    MATH_SYMBOLS: () => MATH_SYMBOLS,

    DROP_LIST_THEMES: () => DROP_LIST_THEMES,

    CHART_TYPES: () => CHART_TYPES,

    MAX_GRADE: () => MAX_GRADE,

    PLURALIZATION_KEYS: () => PLURALIZATION_KEYS,

    isStartTargetWithMilestones() {
      return (
        isKR(this.okrElement) &&
        isStartTargetProgress({ element: this.okrElement }) &&
        !isEmpty(this.okrElement.elementTargetPeriods)
      )
    },

    resolvedPredictedScore() {
      return Math.min(this.predictedScore, MAX_GRADE)
    },

    gradeForChart() {
      return this.roundValue(Math.min(this.grade, MAX_GRADE))
    },

    predictedScoreForChart() {
      return this.roundValue(this.resolvedPredictedScore)
    },

    selectedIntervalDates() {
      return getSelectedIntervalDates(this.okrElement)
    },

    minMaxDates() {
      return getMinMaxDates(
        this.selectedIntervalDates,
        this.okrElement.elementStartDate,
        this.okrElement.dueDate
      )
    },

    gradeValues() {
      return {
        grade: this.gradeForChart,
        predictedScore: this.predictedScoreForChart,
        thresholdBehind: this.settings.thresholdBehind,
        thresholdOnTrack: this.settings.thresholdOnTrack,
        fillThresholdLines: true
      }
    },

    chartData() {
      if (!this.okrElement) {
        return null
      }
      const today = +compensateTimeZone(dayjs(), true)

      const xmin = Math.min(today, +this.minMaxDates.minDate)
      const xmax = Math.max(today, +this.minMaxDates.maxDate)

      const { '--rgb-color': rgbColor } = getGradeColorStyle(this.gradeColor)

      const color = `rgb(${rgbColor})`

      return {
        chartLines: [
          {
            color,
            data: {
              [today]: this.gradeForChart
            }
          },
          {
            color: `rgb(${rgbColor}, 0.5)`,
            data: {
              [xmax]: this.predictedScoreForChart
            }
          },
          {
            color,
            data: {
              [xmin]: null,
              [xmax]: null
            }
          }
        ]
      }
    }
  },

  methods: {
    getSimplifiedNumber,

    getGradeColorVariable,

    roundValue(value) {
      return Math.round(value)
    }
  }
})
</script>

<style lang="scss" scoped>
.si-Trigger {
  --layout-gap: 4px;
  --color: #{$dark-3};
  --icon-color: #{$dark-3};
  --font-weight: #{fw('semi-bold')};
}

.si-TooltipContainer {
  padding: 20px 20px 23px;
  // box-shadow: $common-box-shadow;
  white-space: normal;
}

.si-TooltipTitle {
  font-family: $system-ui;
  font-style: normal;

  font-size: $fs-12;
  line-height: 16px;
  color: $dark-3;

  &:not(:last-child) {
    margin-bottom: 4px;
  }

  span {
    color: var(--color);
    font-weight: fw('bold');
  }

  &:not(&-bold) {
    font-weight: fw('regular');
  }

  &-bold {
    font-weight: fw('bold');
  }
}

.si-Chart {
  margin-top: 12px;
}
</style>
